import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  notification,
  message,
  Skeleton,
} from "antd";
import ProjectSettingToggle from "../ProjectSettingToggle";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchProjectUsers } from "../../services/projectsAPI";
import {
  updateSubmittal,
  fetchNextSubmittalNumber,
} from "../../services/submittalsAPI";
import { useAuth } from "../../hooks/useAuth";
import { toUTCMidnight, parseDateOnlyForPicker } from "../../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../../constants/dateConstants";
import queryClient from "../../queryClient";
import { filterOption } from "../../utils/textUtils";
import { CloseOutlined } from "@ant-design/icons";
import AttachmentsSection from "../AttachmentsSection";
import { fetchVendors } from "../../services/vendorsAPI";
import { getSubmittalById } from "../../services/submittalsAPI";
import { normalizeBooleanValue } from "../../utils/projectUtils";
import ProjectUserSelect from "../ProjectUserSelect";
import SpecSectionSelect from "../SpecSectionSelect";

const { TextArea } = Input;

const UpdateShopDrawingModal = ({ visible, onClose, submittalId }) => {
  const { currentUser, selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const trackMaterialRelease = normalizeBooleanValue(
    selectedProject?.track_material_release
  );
  const [form] = Form.useForm();
  const [reviewSteps, setReviewSteps] = useState([]);
  const [ccSearchValue, setCcSearchValue] = useState("");

  const { data: users } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: vendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: ({ submittalId, submittalData, sendEmail }) =>
      updateSubmittal(submittalId, submittalData, sendEmail),
    onSuccess: () => {
      queryClient.invalidateQueries(["submittals", projectId]);
      onClose();
      form.resetFields();
      notification.success({ message: "Shop Drawing updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update shop drawing",
        description: error.response.data.detail.message,
      });
    },
  });

  const { data: submittal, refetch } = useQuery({
    queryKey: ["submittal", submittalId],
    queryFn: () => getSubmittalById(submittalId),
    enabled: !!submittalId,
    select: (data) => ({
      ...data,
      formatted_number: data.specification_section_details?.section_number
        ? `${data.specification_section_details.section_number}-${data.number}`
        : data.number,
      formatted_ball_in_courts: data?.ball_in_court_details
        ?.map((element) => element.username)
        .join(", "),
      formatted_received_from: data.received_from_details?.username,
      formatted_spec_section:
        data.specification_section_details?.title || "N/A",
    }),
    onError: (error) => {
      console.log("Failed to fetch shop drawing", error);
    },
  });

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  useEffect(() => {
    if (submittal) {
      const initialSteps = submittal.steps?.map((step) => ({
        reviews: step.reviews?.map((review) => ({
          review_id: review._id || null,
          reviewer_id: review.reviewer_id,
        })),
      }));
      setReviewSteps(initialSteps);

      form.setFieldsValue({
        title: submittal.title,
        description: submittal.description,
        type: submittal.type || undefined,
        edms: submittal.edms || undefined,
        due_date: submittal.due_date ? parseDateOnlyForPicker(submittal.due_date) : null,
        issue_date: submittal.issue_date ? parseDateOnlyForPicker(submittal.issue_date) : null,
        released_on: submittal.released_on
          ? parseDateOnlyForPicker(submittal.released_on)
          : null,
        estimated_delivery_date: submittal.estimated_delivery_date
          ? parseDateOnlyForPicker(submittal.estimated_delivery_date)
          : null,
        received_from_id: submittal.received_from_id,
        responsible_contractor_id: submittal.responsible_contractor_id,
        specification_section_id: submittal.specification_section_id,
        submittal_manager_id: submittal.submittal_manager_id,
        number: submittal.number,
        revision: submittal.revision,
        status: submittal.status,
        priority: submittal.priority || "Medium",
        steps: submittal.steps?.map((step) => ({
          reviews: step.reviews?.map((review) => review.reviewer_id),
        })),
        distribution_list: submittal.distribution_list,
        attachments: submittal.attachments || [],
      });
    }
  }, [submittal]);

  const handleChangeReviewer = (values, index) => {
    let _reviewSteps = [...reviewSteps];
    const reviewers = _reviewSteps[index].reviews.map(
      (review) => review.reviewer_id
    );
    const isAddition = values.some(
      (reviewer_id) => !reviewers.includes(reviewer_id)
    );

    if (isAddition) {
      _reviewSteps[index].reviews = [
        ..._reviewSteps[index].reviews,
        {
          reviewer_id: values.find((element) => !reviewers.includes(element)),
          review_id: null,
        },
      ];
    } else {
      _reviewSteps[index].reviews = _reviewSteps[index].reviews.filter(
        (review) => values.includes(review.reviewer_id)
      );
    }

    setReviewSteps(_reviewSteps);
  };

  const handleRemoveStep = (index) => {
    let _reviewSteps = [...reviewSteps];
    _reviewSteps.splice(index, 1);

    setReviewSteps(_reviewSteps);
  };

  const handleAddSubmittal = () => {
    let _reviewSteps = [...reviewSteps, { reviews: [] }];

    setReviewSteps(_reviewSteps);
  };

  const handleUpdateSubmittal = (values) => {
    const submittalData = {
      ...values,
      type: "Shop Drawing",
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
      issue_date: values.issue_date ? toUTCMidnight(values.issue_date) : null,
      released_on: values.released_on ? toUTCMidnight(values.released_on) : null,
      estimated_delivery_date: values.estimated_delivery_date
        ? toUTCMidnight(values.estimated_delivery_date)
        : null,
      steps: reviewSteps?.map((step) => ({
        reviews: step.reviews?.map((review) => ({
          reviewer_id: review.reviewer_id,
          id: review.review_id,
        })),
      })),
      attachments: values.attachments?.map((file) => file) || null,
    };
    mutation.mutate({
      submittalId: submittalId,
      submittalData,
      sendEmail: form.getFieldValue("email_notifications") ?? true,
    });
  };

  const handleSpecSectionChange = (value) => {
    fetchNextSubmittalNumber(projectId, value).then((data) => {
      form.setFieldsValue({ number: data.next_number });
    });
  };

  const handleReceivedFromChange = (value) => {
    const receivedFrom = users?.data?.find((user) => user.id === value);
    if (receivedFrom?.vendor_id) {
      form.setFieldsValue({
        responsible_contractor_id: receivedFrom.vendor_id,
      });
    } else {
      // Clear the responsible contractor field if user has no vendor_id
      form.setFieldsValue({
        responsible_contractor_id: undefined,
      });
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Update Shop Drawing"
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      {!submittal ? (
        <Skeleton active />
      ) : (
        <Form form={form} onFinish={handleUpdateSubmittal} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Number"
                name="number"
                rules={[
                  {
                    required: true,
                    message: "Please input the shop drawing number!",
                  },
                ]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Revision"
                name="revision"
                rules={[
                  { required: true, message: "Please input the revision!" },
                ]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please input the title!" }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Specification Section"
                name="specification_section_id"
                getValueFromEvent={(value) => {
                  handleSpecSectionChange(value);
                  return value;
                }}
              >
                <SpecSectionSelect className="w-full" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Shop Drawing Manager"
                name="submittal_manager_id"
                rules={[
                  {
                    required: true,
                    message: "Please select the shop drawing manager!",
                  },
                ]}
              >
                <ProjectUserSelect
                  mode={null}
                  placeholder="Select shop drawing manager"
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Please select the status!" }]}
              >
                <Select>
                  <Select.Option value="Open">Open</Select.Option>
                  <Select.Option value="Closed">Closed</Select.Option>
                  <Select.Option value="Draft">Draft</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="EDMS" name="edms">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Priority" name="priority" className="w-full">
            <Select
              options={[
                { value: "High", label: "High" },
                { value: "Medium", label: "Medium" },
                { value: "Low", label: "Low" },
              ]}
              className="w-full"
            />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <TextArea />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Issue Date" name="issue_date" className="w-full">
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Due Date" name="due_date" className="w-full">
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
          </Row>
          {trackMaterialRelease && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Released On" name="released_on">
                  <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Estimated Delivery Date"
                  name="estimated_delivery_date"
                >
                  <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Received From"
                name="received_from_id"
                getValueFromEvent={(value) => {
                  handleReceivedFromChange(value);
                  return value;
                }}
              >
                <ProjectUserSelect
                  mode={null}
                  allowClear
                  placeholder="Select received from"
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Responsible Contractor"
                name="responsible_contractor_id"
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption}
                  options={vendors?.vendors?.map((vendor) => ({
                    value: vendor.id,
                    label: vendor.name,
                  }))}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Cc" name="distribution_list" className="w-full">
            <ProjectUserSelect
              searchValue={ccSearchValue}
              onSearch={setCcSearchValue}
              placeholder="Select Cc"
              className="w-full"
              allowClear
            />
          </Form.Item>
          <Form.Item name="attachments" label="Attachments" className="w-full">
            <AttachmentsSection projectId={projectId} />
          </Form.Item>
          <Form.Item>
            {reviewSteps.map((step, index) => {
              return (
                <div className="my-5" key={index}>
                  <label className="mr-2">Step #{index + 1}</label>
                  <div className="flex items-center">
                    <ProjectUserSelect
                      mode="multiple"
                      placeholder="Select reviewers"
                      value={step.reviews.map((review) => review.reviewer_id)}
                      onChange={(value) => {
                        handleChangeReviewer(value, index);
                      }}
                      className="flex-grow"
                    />
                    <Button
                      type="danger"
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveStep(index)}
                      className="ml-2"
                    />
                  </div>
                </div>
              );
            })}
            <Button type="dashed" onClick={handleAddSubmittal} className="w-full">
              Add Reviewer
            </Button>
          </Form.Item>
          <div className="project-settings-grid mt-10">
            <ProjectSettingToggle name="email_notifications" title="Email Notifications" initialValue={true} />
          </div>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isPending}
              style={{ marginTop: "16px", width: "100%" }}
            >
              {form.getFieldValue("email_notifications") ?? true
                ? "Update Shop Drawing and Send Emails"
                : "Update Shop Drawing"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UpdateShopDrawingModal;
